import {
  FileText, Users, CalendarCheck, Linkedin, MessageSquare, Radio, MonitorPlay, Sparkles,
} from "lucide-react";

const CAPABILITIES = [
  {
    icon: FileText,
    title: "Content Syndication",
    metric: "2.4M imp/wk",
    desc: "Targeted asset distribution across vertical audiences to capture intent at top-of-funnel.",
    image: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=900&q=70",
  },
  {
    icon: Users,
    title: "SQL & BANT Leads",
    metric: "9.3K SQLs/wk",
    desc: "Sales-ready leads validated against Budget, Authority, Need, and Timeline.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=70",
  },
  {
    icon: CalendarCheck,
    title: "Appointment Setting",
    metric: "68 meetings/wk",
    desc: "Booked meetings with decision-makers, ready for your sales team to convert.",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=70",
  },
  {
    icon: Linkedin,
    title: "LinkedIn & Paid Social",
    metric: "5.7% CTR",
    desc: "Precision ABM outreach and content amplification across the platforms that matter.",
    image: "https://images.unsplash.com/photo-1611944212129-29977ae1398c?auto=format&fit=crop&w=900&q=70",
  },
  {
    icon: MessageSquare,
    title: "WhatsApp & SMS",
    metric: "95%+ open rate",
    desc: "Signal-triggered conversational nurture with 95%+ open rates on WhatsApp Business.",
    image: "https://images.unsplash.com/photo-1611926653458-09294b3142bf?auto=format&fit=crop&w=900&q=70",
  },
  {
    icon: Radio,
    title: "Webinars & LIVE Events",
    metric: "1,412 reg/event",
    desc: "Curated executive experiences that convert intent into measurable pipeline acceleration.",
    image: "https://images.unsplash.com/photo-1591115765373-5207764f72e7?auto=format&fit=crop&w=900&q=70",
  },
  {
    icon: MonitorPlay,
    title: "Programmatic Display",
    metric: "480K clicks/mo",
    desc: "Predictor-driven placements that follow real intent, not static demographic segments.",
    image: "https://images.unsplash.com/photo-1522542550221-31fd19575a2d?auto=format&fit=crop&w=900&q=70",
  },
  {
    icon: Sparkles,
    title: "Lead Scoring & Intelligence",
    metric: "1.8s / lead",
    desc: "One scoring engine across every channel — your pipeline becomes a priority list.",
    image: "https://images.unsplash.com/photo-1590650153855-d9e808231d41?auto=format&fit=crop&w=900&q=70",
  },
];

export default function Capabilities() {
  return (
    <section id="capabilities" data-testid="capabilities-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
            Core Capabilities
          </div>
          <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[64px] leading-[0.98] tracking-[-0.02em]">
            Every lever we pull, <span className="italic font-normal text-teal">in one place.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Eight services under one accountable partner — from top-of-funnel
            awareness to BANT-qualified booked meetings.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {CAPABILITIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                data-testid={`capability-${c.title.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className="group relative rounded-2xl border border-white/[0.06] bg-[#0A121A] overflow-hidden card-hover"
              >
                {/* Animated image cover */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={c.image}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover opacity-45 group-hover:opacity-70 group-hover:scale-110 transition-all duration-[1400ms] ease-out capability-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A121A] via-[#0A121A]/60 to-transparent" />
                  <div className="absolute top-3 left-3 h-9 w-9 rounded-lg bg-teal/15 border border-teal/30 grid place-items-center text-teal backdrop-blur-md">
                    <Icon size={16} />
                  </div>
                  <div className="absolute top-3 right-3 text-[10px] font-mono text-white/70 backdrop-blur px-2 py-0.5 rounded-full bg-black/40">
                    {String(i + 1).padStart(2, "0")} / 08
                  </div>
                  <div className="absolute bottom-3 left-3 text-[11px] font-mono text-teal">
                    {c.metric}
                  </div>
                  {/* subtle scan line */}
                  <div className="scan-line pointer-events-none" />
                </div>

                {/* Content */}
                <div className="p-5 md:p-6">
                  <h3 className="font-display text-lg font-semibold mb-2 leading-tight">
                    {c.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {c.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .capability-image {
          filter: grayscale(0.35) hue-rotate(-8deg) contrast(1.05);
          transition: filter 900ms ease, opacity 900ms ease, transform 1400ms ease;
        }
        .group:hover .capability-image {
          filter: grayscale(0) hue-rotate(0) contrast(1.15);
        }
        .scan-line {
          position: absolute; inset: 0;
          background: linear-gradient(180deg, transparent 0%, transparent 40%, rgba(0,229,194,0.06) 50%, transparent 60%);
          background-size: 100% 300%;
          animation: scan 6s linear infinite;
          mix-blend-mode: screen;
          opacity: 0.7;
        }
        @keyframes scan {
          0% { background-position: 0 -100%; }
          100% { background-position: 0 200%; }
        }
      `}</style>
    </section>
  );
}
